import { faCheck, faCircleXmark, faShieldBlank } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Button, Card, Container, Hidden, IconButton, makeStyles, Toolbar, Typography } from '@material-ui/core';
import { blue, grey } from '@material-ui/core/colors';
import { ArrowBackIos, Close } from '@material-ui/icons';
import clsx from 'clsx';
import isEmpty from 'lodash.isempty';
import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { getCovidHistoryLatest } from 'services/provider';
import { BrandonG, HelvNeue } from 'themes/theme';
import { personNameFormal, personNameFull } from 'util/format';
import { localizeNumber } from 'util/localizeFormat';
import { useState } from 'util/stateref';
import { PROVIDER_CACHE } from '../Details/BookingDetail/Provider';
import { bookingState } from '../RecoilState';
import { Infocard } from './InfoCard';
import { StackedGreenShieldCheck, StackedRedShieldError } from './StackedStatusIcon';

const useStyles = makeStyles((theme) => {
  return {
    top: {
      bottom: 'auto',
      top: 0,
      right: 'auto',
    },
    appBar: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      backgroundColor: 'transparent',
    },
    toolbar: {
      display: 'flex',
      justifyContent: 'space-evenly',
    },
    title: {
      flexGrow: 1
    },
    root: {
      width: '100%',
      height: '100%',
      minHeight: '100vh',
      position: 'relative',
      zIndex: 0,
      backgroundColor: theme.palette.background.paper,
    },
    gradient: {
      background: "linear-gradient(228.07deg, #7F8AAC -1.37%, #74BACD 106.99%)",
      // background: "linear-gradient(44.4deg, #BAE1CD 1.03%, #86CAC7 100%)",
      position: 'absolute',
      top: 0,
      height: '50vh',
      [theme.breakpoints.up("md")]: {
        height: 300,
      },
      width: '100vw',
      zIndex: -1
    },
    container: {
      [theme.breakpoints.down("sm")]: {
        padding: 0
      }

    },
    profileHeader: {
      position: 'relative',
    },
    avatar: {
      borderRadius: '50%',
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      borderStyle: 'solid',
      objectFit: 'cover',
      marginBottom: 30,
      width: 125,
      height: 125,
      boxShadow: theme.shadows[24],
      [theme.breakpoints.up("md")]: {
        width: '200px',
        height: '200px',
        boxShadow: theme.shadows[0],
      },

    },
    avatarPlaceholder: {
      display: 'none',
      [theme.breakpoints.up("md")]: {
        display: 'block',
        boxShadow: theme.shadows[24],
        backgroundColor: blue[100],
        position: 'absolute',
        alignItems: 'center',
        top: -35,
        zIndex: -1
      }
    },

    providerBio: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      [theme.breakpoints.up("md")]: {

        flexDirection: "row",
        justifyContent: "space-between",
        flexWrap: "no-wrap",
        paddingBottom: 40,
      }
    },
    statistics: {
      "& > *": {
        [theme.breakpoints.up("md")]: {
          marginBottom: theme.spacing(3),
        },
      },
      [theme.breakpoints.down("sm")]: {
        display: 'flex',
        flexDirection: 'row'
      }
    },
    stat: {
      [theme.breakpoints.down("md")]: {
        flexBasis: '100%',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
      },
      display: "flex",
      flexDirection: "column",
      alignContent: "center",
      justifyContent: "center",
      alignItems: "center",
      [theme.breakpoints.up("md")]: {
        marginBottom: "10px",
        borderRadius: 15,
        boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0",
        width: 150,
        height: 150,
      },


    },
    statTotal: {
      background: "#A6C1D6",
      [theme.breakpoints.up("md")]: {
        background: "rgba(116, 186, 205, 1)",
      }
    },
    statAverage: {
      background: "#96B6CF",
      [theme.breakpoints.up("md")]: {
        background: "rgba(134, 202, 199, 1)",
      }
    },
    statFavorite: {
      background: "#82AAC9",
      [theme.breakpoints.up("md")]: {
        background: "rgba(138, 173, 209, 1)",
      }
    },
    infoCard: {
      backgroundColor: theme.palette.background.paper,
      height: '100%'
    },
    thinHeader: {
      fontFamily: BrandonG,
      color: "white",
      fontWeight: 300,
      fontSize: theme.typography.h2.fontSize,
      [theme.breakpoints.up("md")]: {
        fontSize: theme.typography.h1.fontSize,
      }
    },
    subHeader: {
      fontFamily: HelvNeue,
      color: "white",
      fontWeight: 300,
      fontSize: theme.typography.body1.fontSize,
      [theme.breakpoints.up("md")]: {
        fontSize: theme.typography.h4.fontSize,
      }
    },
    headerText: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",

      [theme.breakpoints.up("md")]: {
        paddingLeft: 250,
        alignItems: "flex-start",
      }
    },
    verticalInfo: {
      [theme.breakpoints.up("md")]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: 'absolute',
        alignItems: 'center',
        top: 30
      }
    },
    statValue: {
      color: "white",
      fontWeight: 300,
    },
    statLabel: {
      color: "white",
    },
    verticalInfoPadded: {
      [theme.breakpoints.up("md")]: {
        paddingLeft: 250
      },
      [theme.breakpoints.down("sm")]: {
        padding: 0
      }
    },
    testResults: {
      paddingTop: theme.spacing(4),
      display: 'flex',
      [theme.breakpoints.down("sm")]: {
        padding: 0,
        color: 'white',
        fontWeight: 400,
      },
      fontFamily: HelvNeue,
      textTransform: 'uppercase',

    }
  }
})

const CovidStatusShield = ({ status }) => {
  switch (status) {
    case 'No Symptoms':
      return <><StackedGreenShieldCheck /> Covid tested - {status} </>
    case 'Suspected':
      return <><StackedGreenShieldCheck /> Covid tested - {status} </>
    case 'Probable':
      return <><StackedGreenShieldCheck /> Covid tested - {status} </>
    case 'Confirmed':
      return <><StackedRedShieldError /> Covid tested - {status} </>
    default:
      return <></>

  }

}
const ProviderProfile = ({ }) => {
  const classes = useStyles()
  const { provider } = { ...useRecoilValue(bookingState) }
  const booking = useRecoilValue(bookingState)
  const history = useHistory()
  const [covidStatusValue, setCovidStatusValue, covidStatusValueRef] = useState();
  const [providerData, setProviderData] = useState();

  useMemo(async () => {
    if (provider?.id) {
      const covidStatusResult = await getCovidHistoryLatest(provider?.id)
      console.log("📢[index.jsx:255]: ", covidStatusResult);
      setCovidStatusValue(covidStatusResult?.assessment?.title)
    }
  }, [provider?.id]);


  const providerName = useMemo(() => {
    const name = personNameFull(provider)
    const p = PROVIDER_CACHE[provider?.provider_type]
    const title = [p?.prefix, name, p?.suffix].filter(t => !isEmpty(t)).join(' ')
    return title

  }, [provider]);


  return (
    <>
      <div className={clsx(classes.root)}>
        <div className={clsx(classes.gradient)} />

        <AppBar elevation={0} position="static" className={clsx(classes.top, classes.appBar)}>
          <Toolbar>
            <IconButton edge="start" color="default" onClick={() => history.replace(`/bookings/${booking?.id}` + history.location.search)} aria-label="close">
              <FontAwesomeIcon icon={faCircleXmark} inverse />
            </IconButton>
            <Typography variant="h6" className={classes.title}>
            </Typography>


          </Toolbar>
        </AppBar>

        <Container maxWidth="md" className={classes.container}>
          <div className={clsx(classes.profileHeader)}>
            <div className={clsx(classes.avatarPlaceholder, classes.avatar)} />
            <div className={clsx(classes.providerBio)} >
              <Hidden mdUp>
                <img className={clsx(classes.avatar)} src={provider?.profile_pic_url} />
              </Hidden>
              <div className={clsx(classes.headerText)}>
                <Typography gutterBottom className={classes.thinHeader} variant="h1" color="textPrimary">{providerName}</Typography>
                <Typography gutterBottom className={classes.subHeader} variant="h4" color="textPrimary">{PROVIDER_CACHE[provider?.provider_type]?.label}</Typography>
              </div>
              <Hidden mdUp>
                <Typography gutterBottom variant="h6" color="textPrimary" component="p" className={classes.testResults}>
                  <CovidStatusShield status={covidStatusValue} />
                </Typography>
              </Hidden>
            </div>
          </div>
          <div className={classes.verticalInfo}>
            <Hidden smDown>
              <img className={clsx(classes.avatar)} src={provider?.profile_pic_url} />
            </Hidden>
            <div className={classes.statistics}>
              <div className={clsx(classes.statTotal, classes.stat)}>
                <Typography variant="h2" className={classes.statValue}>{localizeNumber(9999)}</Typography>
                <Typography className={classes.statLabel}>Total Jobs</Typography>
              </div>
              <div className={clsx(classes.statAverage, classes.stat)}>
                <Typography variant="h2" className={classes.statValue}>{localizeNumber(provider?.rating, true)} <FontAwesomeIcon size="2xs" icon="star" /></Typography>
                <Typography className={classes.statLabel}>Ave. Rating</Typography>
              </div>
              <div className={clsx(classes.statFavorite, classes.stat)}>
                <Typography variant="h2" className={classes.statValue}>{localizeNumber(9999)}</Typography>
                <Typography className={classes.statLabel}>Favorited by</Typography>
              </div>
            </div>
          </div>
        </Container>
        <div className={classes.infoCard}>
          <Container maxWidth="md" className={classes.verticalInfoPadded}>
            <Hidden smDown>
              <Typography variant="h4" color="textPrimary" component="p" className={classes.testResults}>
                <CovidStatusShield status={covidStatusValue} />
              </Typography>
            </Hidden>
            <Infocard />
          </Container>
        </div>
      </div>
    </>
  )
}

export { ProviderProfile };
